import { forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, Grid, Box, useMediaQuery } from '@mui/material'
import FooterVisionDelNegocio from './FooterVisionDelNegocio'
import { useBusinessInsightContext } from './VisionContext/businessInsightContext'
import BarChartER from './CharComponent/ChartBar'
import SimpleBarChart from './CharComponent/ChartBarBank'
import iconoEnlace from '../../../../../../assets/iconos/iconEnlace.png'
import { Paper, DashboardCard, KpiCard, SectionTitle } from '../../../../ui'
import CustomButton from '../../../../ui/CustomButton'
import Draggable from 'react-draggable'
import Lottie from 'lottie-react'
import LoaderMain from '../../../../../../assets/svgIcons/loaderMain.json'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import ChartStackedAging from './CharComponent/ChartStackedAging'
import QuestionIcon from '@mui/icons-material/HelpOutline'
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import { styled, useTheme } from '@mui/material/styles'
import ResizableModal from '../../../../ui/ResizableModal'
import ListEstadoDeResultadosExtend from '../../../../../../pages/Reportes/estadoDeResultadoExtendido/ListEstadoDeResultadosExtend'
import { useResumenF29 } from '../ResumenImpuestos/useResumenF29'
import GlobalContext from '../../../../../../context/global-context'
import { BusinessAPI } from '../../../../../../api/business.api'
import { useSidebarContext } from '../../../../../layouts/sideBar/SidebarContext'
import ExecutiveSummaryView from './components/ExecutiveSummaryView'
import EstadoResultadoResumenTable from './components/EstadoResultadoResumenTable'
import { InitialBalancesModal } from './components/InitialBalancesModal'
import CashFlowDueLineChart from './components/CashFlowDueLineChart'
import {
  TopCobranzasMiniCards,
  CardInfo,
  BarChartKPIs,
  SemiPieCard,
  SimpleChartCard,
  CashFlowSummaryPanel
} from './components/VisionCards'
import {
  isYYYYMM,
  isDayMonth,
  parseDisplayNumber,
  formatCLPFull,
  formatCLP_M,
  EMPTY_GROUP_SUMMARY,
  getTopGroupedAccounts,
  formatDecimalDisplay,
  formatBaseDisplayValue,
  takeLastN,
  lowerVisibleCount,
  KPI_NUMBER_CLASS,
  getMonthLabel
} from './utils/visionDelNegocio.utils'
import { useInitialBalancesController } from './hooks/useInitialBalancesController'

const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip
      {...props}
      classes={{ popper: className }}
      disableInteractive
      enterTouchDelay={500}
    />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: 'rgb(var(--primary))',
      color: 'rgb(var(--surface))',
      fontSize: '0.75rem',
      padding: '6px 12px',
      borderRadius: '6px',
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: 'rgb(var(--primary))',
    },
  }))

const DraggablePaper = forwardRef(function DraggablePaper(props, ref) {
  const nodeRef = useRef(null)
  const handleRef = useCallback(
    instance => {
      nodeRef.current = instance
      if (typeof ref === 'function') ref(instance)
      else if (ref) ref.current = instance
    },
    [ref]
  )
  return (
    <Draggable
      handle='#draggable-dialog-title'
      cancel={'[class*="MuiDialogContent-root"]'}
      bounds='parent'
      nodeRef={nodeRef}
    >
      <Paper ref={handleRef} {...props} />
    </Draggable>
  )
})

dayjs.extend(isoWeek)

export const VisionDelNegocio = ({onGoToResumen}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isCompact = useMediaQuery(theme.breakpoints.down('md'))
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'))
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'))
  const isXLUp = useMediaQuery(theme.breakpoints.up('xl'))
  const isShort = useMediaQuery('(max-height: 800px)')
  const { isExpanded: isSidebarExpanded, isPinned } = useSidebarContext()
  const isSidebarConstrained = (isSidebarExpanded || isPinned) && !isMobile && !isXLUp
  const isNarrow = isMobile || isCompact || isSidebarConstrained
  const shouldCompactValues = !isXLUp || isSidebarConstrained
  const baseMonthsToShow = isMobile ? 3 : isCompact ? 6 : isLgDown ? 8 : 12
  const monthsToShow = isSidebarConstrained ? lowerVisibleCount(baseMonthsToShow) : baseMonthsToShow
  const spacingZeroOrTwo = isSmUp ? (isShort ? 1 : 2) : 0
  const spacingOneOrTwo = isSmUp ? (isShort ? 1 : 2) : 1
  const formatCLPDisplay = useCallback(value => {
    const numericValue = parseDisplayNumber(value)
    if (shouldCompactValues && Math.abs(numericValue) >= 1_000_000) {
      return formatCLP_M(value)
    }
    return formatBaseDisplayValue(value)
  }, [shouldCompactValues])
  const numberUi = useMemo(() => ({
    shouldCompactValues,
    isSidebarConstrained,
    isCompact,
    isNarrow,
    formatCLPDisplay,
    formatCLPFull,
    parseDisplayNumber,
    kpiNumberClass: KPI_NUMBER_CLASS
  }), [shouldCompactValues, isSidebarConstrained, isCompact, isNarrow, formatCLPDisplay])

  const {
    data,
    loading,
    error,
    hasLoadedData,
    refetch
  } = useBusinessInsightContext()

  const { currentBusiness, ui } = useContext(GlobalContext)
  const businessId = Number(currentBusiness?.id || 0)
  const businessAPI = useMemo(() => new BusinessAPI(), [])
  const initialBalancesController = useInitialBalancesController({ businessId, currentBusiness })
  const {
    initialBalanceRows,
    hasInitialBalances,
    initialBalancesLoading,
    handleOpenInitialBalanceModal
  } = initialBalancesController

  const { summaryItems } = useResumenF29({
    businessId: currentBusiness?.id,
    businessAPI,
    ui,
  })
  const f29ProjectedValue = summaryItems?.find(i => i.key === 'total_pagar')?.value

  const meses = [ 'Enero','Febrero','Marzo','Abril','Mayo','Junio', 'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']
  const mesActual = meses[new Date().getMonth()]
  const cardPaddingClass = isShort ? 'p-3 sm:p-4 md:p-5' : 'p-4 sm:p-6 md:p-8'
  const sectionCardClass = `shadow-card rounded-card bg-surface border border-border ${cardPaddingClass}`
  const smallCardBase = isShort
    ? 'h-full min-h-[170px] sm:min-h-[190px] md:min-h-[210px]'
    : 'h-full min-h-[200px] sm:min-h-[220px] md:min-h-[240px]'
  const DEBUG_UNBOUNDED_MOBILE =
    typeof window !== 'undefined' && window.location?.hostname === 'localhost'
  const chartScrollSx = {
    width: '100%',
    maxWidth: '100%',
    overflowX: { xs: 'auto', sm: 'hidden' },
    overflowY: DEBUG_UNBOUNDED_MOBILE ? { xs: 'visible', sm: 'visible' } : { xs: 'auto', sm: 'visible' },
    maxHeight: DEBUG_UNBOUNDED_MOBILE ? { xs: 'none', sm: 'none' } : { xs: '70vh', sm: 'none' },
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
  }
  const chartScrollSxNoYScroll = {
    ...chartScrollSx,
    overflowY: { xs: 'visible', sm: 'visible' },
    maxHeight: { xs: 'none', sm: 'none' },
  }
  const chartInnerSx = (minWidth, minHeight = 240) => {
    void minWidth
    return {
      width: '100%',
      minWidth: 0,
      maxWidth: '100%',
      minHeight: { xs: minHeight, sm: isShort ? Math.min(minHeight, 240) : 300 },
    }
  }
  const dialogPaperSx = {
    width: { xs: '100%', md: 1200 },
    height: { xs: '100%', md: 580 },
    maxWidth: { xs: '100%', md: '96vw' },
    maxHeight: { xs: '100%', md: '90vh' },
    borderRadius: { xs: 0, md: 6 },
    overflow: { xs: 'auto', md: 'hidden' },
  }
  const tooltipIconClass = isCompact
    ? 'text-muted cursor-pointer hover:text-text mt-1'
    : 'text-muted cursor-pointer hover:text-text mt-8'

  const {
    summaryDataClientes,
    agingChartData,
    monthlySummaryFromCodes,
    estadoResultadoResumen,
    erIngresosMesActual,
    erGastosMesActual,
    erIngresosUlt3,
    erGastosUlt3,
    erIngresosAnual,
    erGastosAnual,
    porcentajeValor,
    totalSaldoAcumulado,
    monthlyChartData,
    weeklyChartData,
    receivablesDueChartData,
    vencidoPorCobrar,
    vencidoPorPagar,
    totales,
    promedioMensual,
    runAway,
    resultado,
    erByMonthFromCodes,
    weeklyERSummary,
  } = data
  const [openModal, setOpenModal] = useState(false)
  const [openModalBanco, setOpenModalBanco] = useState(false)
  const [openModalEstado, setOpenModalEstado] = useState(false)
  const [activeDashboardView] = useState('kpis') //setActiveDashboardView
  const isSummaryView = activeDashboardView === 'summary'
  const noopOnSelectMonth = useCallback(() => {}, [])
  const closeERModal = useCallback(() => setOpenModal(false), [])
  const closeBancoModal = useCallback(() => setOpenModalBanco(false), [])
  const closeEstadoModal = useCallback(() => setOpenModalEstado(false), [])

  // Estados separados
  const [erSelectedPeriod, setErSelectedPeriod] = useState(null)
  const [erSelectedData, setErSelectedData] = useState({
    ingresosOriginal: 0,
    gastosOriginal: 0,
    totalOriginal: 0
  })
  const [bankSelectedPeriod, setBankSelectedPeriod] = useState(null)


  const [bankTimeframe, setBankTimeframe] = useState('month')
  const handleBankTimeframe = useCallback((_, val) => {
    if (val) setBankTimeframe(val)
  }, [])

  const porcentajeLabel = useMemo(
    () => `${porcentajeValor >= 0 ? '+' : ''}${porcentajeValor?.toFixed(2)}%`,
    [porcentajeValor]
  )

  const weeklyER = useMemo(
    () => weeklyERSummary?.[erSelectedPeriod],
    [erSelectedPeriod, weeklyERSummary]
  )
  const receivablesSummary = useMemo(() => {
    const source = summaryDataClientes?.data ?? summaryDataClientes ?? {}
    const cobrado = Number(source.cobrado ?? source.pagado ?? 0)
    const porCobrar = Number(source.porCobrar ?? source.saldo ?? 0)
    const notasCredito = Number(source.totalNC ?? 0)
    const totalGestionado = cobrado + porCobrar
    const tasaCobranza = totalGestionado > 0 ? Math.round((cobrado / totalGestionado) * 100) : 0

    return {
      cobrado,
      porCobrar,
      notasCredito,
      tasaCobranza,
    }
  }, [summaryDataClientes])
  const clientSummary = resultado?.Cliente || EMPTY_GROUP_SUMMARY
  const providerSummary = resultado?.Proveedores || EMPTY_GROUP_SUMMARY
  const topClientAccounts = useMemo(() => getTopGroupedAccounts(clientSummary), [clientSummary])
  const topProviderAccounts = useMemo(() => getTopGroupedAccounts(providerSummary), [providerSummary])
  const currentMonthYearLabel = useMemo(() => {
    const label = new Intl.DateTimeFormat('es-CL', { month: 'long', year: 'numeric' }).format(new Date())
    return label.charAt(0).toUpperCase() + label.slice(1)
  }, [])
  const averageIncomeLast3 = useMemo(
    () => (parseDisplayNumber(erIngresosUlt3) > 0 ? parseDisplayNumber(erIngresosUlt3) / 3 : 0),
    [erIngresosUlt3]
  )
  const averageExpenseLast3 = useMemo(
    () => (parseDisplayNumber(erGastosUlt3) > 0 ? parseDisplayNumber(erGastosUlt3) / 3 : 0),
    [erGastosUlt3]
  )
  const incomeDeltaPct = useMemo(() => {
    if (averageIncomeLast3 <= 0) return 0
    return ((parseDisplayNumber(erIngresosMesActual) - averageIncomeLast3) / averageIncomeLast3) * 100
  }, [averageIncomeLast3, erIngresosMesActual])
  const expenseDeltaPct = useMemo(() => {
    if (averageExpenseLast3 <= 0) return 0
    return ((parseDisplayNumber(erGastosMesActual) - averageExpenseLast3) / averageExpenseLast3) * 100
  }, [averageExpenseLast3, erGastosMesActual])
  const topTwoClientShare = useMemo(() => {
    const topTwoTotal = topClientAccounts
      .slice(0, 2)
      .reduce((acc, item) => acc + Number(item.amount || 0), 0)
    const total = Number(clientSummary.montoTotalGeneral || 0)
    if (total <= 0) return 0
    return Math.round((topTwoTotal / total) * 100)
  }, [clientSummary.montoTotalGeneral, topClientAccounts])
  const runwayMonths = useMemo(() => parseDisplayNumber(runAway), [runAway])
  const volatilityLabel = useMemo(() => {
    const volatility = Math.abs(incomeDeltaPct)
    if (volatility >= 25) return 'Se mueve mucho'
    if (volatility >= 10) return 'Se mueve normal'
    return 'Se mueve poco'
  }, [incomeDeltaPct])
  const executiveScore = useMemo(() => {
    let score = 55

    score += Math.max(Math.min(porcentajeValor, 18), -18)
    score += totalSaldoAcumulado > 0 ? 10 : totalSaldoAcumulado < 0 ? -10 : 0
    score += parseDisplayNumber(totales?.diferencia) > 0 ? 8 : parseDisplayNumber(totales?.diferencia) < 0 ? -8 : 0
    score += Math.round(receivablesSummary.tasaCobranza * 0.12)
    score += runwayMonths >= 1 ? 8 : runwayMonths >= 0.5 ? 4 : 0

    return Math.max(0, Math.min(100, Math.round(score)))
  }, [porcentajeValor, totalSaldoAcumulado, totales?.diferencia, receivablesSummary.tasaCobranza, runwayMonths])
  const executiveStatus = useMemo(() => {
    if (executiveScore >= 75) return 'Sano'
    if (executiveScore >= 55) return 'En observacion'
    return 'Atencion'
  }, [executiveScore])
  const executiveAccent = useMemo(() => {
    if (executiveScore >= 75) return 'rgb(var(--success))'
    if (executiveScore >= 55) return '#E7A817'
    return 'rgb(var(--danger))'
  }, [executiveScore])
  const executiveScoreMeaning = useMemo(() => {
    if (executiveScore >= 75) {
      return 'Eso significa que, hoy, el negocio se ve fuerte y con buen margen para operar.'
    }
    if (executiveScore >= 55) {
      return 'Eso significa que el negocio esta estable, pero conviene mirarlo de cerca.'
    }
    return 'Eso significa que hoy el negocio necesita atencion y seguimiento.'
  }, [executiveScore])
  const executiveGoodBullet = useMemo(() => {
    if (totalSaldoAcumulado > 0) {
      return 'hoy el negocio se ve sano y esta dejando resultado positivo.'
    }
    if (totalSaldoAcumulado < 0) {
      return 'ya tienes una lectura clara de que el negocio necesita ajuste, y eso permite actuar a tiempo.'
    }
    return 'hoy el negocio esta estable y sin una desviacion fuerte en el resultado.'
  }, [totalSaldoAcumulado])
  const executiveImportantBullet = useMemo(() => {
    if (receivablesSummary.porCobrar > 0) {
      return `aun queda por cobrar $ ${formatCLPDisplay(receivablesSummary.porCobrar)}. Cobrar eso mejora tu caja y te da mas aire para operar.`
    }
    if (runwayMonths < 1) {
      return `la caja alcanza para ${formatBaseDisplayValue(runwayMonths)} meses. Conviene mirar ingresos y gastos de cerca.`
    }
    if (parseDisplayNumber(totales?.diferencia) < 0) {
      return 'hoy esta saliendo mas dinero del que entra. Conviene revisar gastos y ritmo de cobro.'
    }
    return 'hoy no hay una alerta fuerte, pero vale la pena seguir de cerca el movimiento del mes.'
  }, [receivablesSummary.porCobrar, runwayMonths, totales?.diferencia, formatCLPDisplay])
  const executiveNextStepBullet = useMemo(() => {
    if (receivablesSummary.porCobrar > 0) {
      return "Siguiente paso: entra a 'Ver clientes / cuentas por cobrar' y prioriza recordatorios o cobros pendientes."
    }
    if (expenseDeltaPct > 10) {
      return "usa 'Ver comprobantes' y revisa si hubo gastos fuera de lo normal."
    }
    return "revisa 'Ver documentos / ventas' para confirmar que el ritmo actual se mantenga."
  }, [receivablesSummary.porCobrar, expenseDeltaPct])
  const incomeDeltaLabel = useMemo(
    () => `${incomeDeltaPct >= 0 ? '+' : ''}${formatDecimalDisplay(incomeDeltaPct)}% vs promedio 3 meses`,
    [incomeDeltaPct]
  )
  const expenseDeltaLabel = useMemo(
    () => `${expenseDeltaPct >= 0 ? '+' : ''}${formatDecimalDisplay(expenseDeltaPct)}% vs promedio 3 meses`,
    [expenseDeltaPct]
  )
  const executiveFactors = useMemo(() => ([
    {
      title: 'Ingresos del mes',
      value: formatCLPDisplay(erIngresosMesActual),
      detail: incomeDeltaLabel,
      action: 'Ver documentos / ventas',
      toneClass: incomeDeltaPct >= 0 ? 'text-primary' : 'text-danger',
    },
    {
      title: 'Gastos del mes',
      value: formatCLPDisplay(erGastosMesActual),
      detail: expenseDeltaLabel,
      action: 'Ver comprobantes',
      toneClass: expenseDeltaPct <= 0 ? 'text-primary' : 'text-danger',
    },
    {
      title: 'CxC pendientes',
      value: formatCLPDisplay(receivablesSummary.porCobrar),
      detail: `${receivablesSummary.tasaCobranza}% recuperado`,
      action: 'Ver clientes / cuentas por cobrar',
      toneClass: 'text-danger',
    },
    {
      title: 'F29 proyectado',
      value: f29ProjectedValue != null ? `$ ${formatCLPDisplay(f29ProjectedValue)}` : '$ 0',
      detail: 'Proyeccion tributaria actual',
      action: 'Ver F29 / IVA',
      toneClass: 'text-text',
    },
    {
      title: 'Concentracion alta',
      value: topTwoClientShare ? `${topTwoClientShare}% ingresos` : 'Sin concentracion relevante',
      detail: topTwoClientShare ? 'Top 2 clientes del periodo' : 'Base de clientes mas distribuida',
      action: 'Ver ranking de clientes',
      toneClass: topTwoClientShare >= 60 ? 'text-danger' : 'text-text',
    },
  ]), [
    erIngresosMesActual,
    incomeDeltaLabel,
    incomeDeltaPct,
    erGastosMesActual,
    expenseDeltaLabel,
    expenseDeltaPct,
    receivablesSummary.porCobrar,
    receivablesSummary.tasaCobranza,
    f29ProjectedValue,
    topTwoClientShare,
    formatCLPDisplay,
  ])
  const executiveFactorExplainers = {
    'Ingresos del mes': 'Dinero que entro este mes por ventas o ingresos. Comparado con tu promedio de 3 meses.',
    'Gastos del mes': 'Dinero que salio este mes por compras o gastos. Si ves 0 y hubo gastos, puede faltar registro o clasificacion.',
    'CxC pendientes': 'Ventas hechas pero aun no pagadas. Cobrar esto mejora tu caja.',
    'F29 proyectado': 'Estimacion de impuestos del mes (no oficial). Usalo como alerta.',
    'Concentracion alta': 'Si pocos clientes explican gran parte de tus ingresos. Mas concentracion = mas riesgo.',
  }

  // --- Handlers / labels ---
  const erMonthLabel = useMemo(() => getMonthLabel(erSelectedPeriod), [erSelectedPeriod])

  const handleSelectMonthER = useCallback(
    payload => {
      const name = typeof payload === 'string' ? payload : payload?.name
      if (!name) return
      const monthData = erByMonthFromCodes[name] || { ingresos: 0, gastos: 0 }
      const nextSelectedData = {
        ingresosOriginal: monthData.ingresos,
        gastosOriginal: monthData.gastos,
        totalOriginal: monthData.ingresos - monthData.gastos
      }
      setErSelectedPeriod(prev => (prev === name ? prev : name))
      setErSelectedData(prev => (
        prev.ingresosOriginal === nextSelectedData.ingresosOriginal &&
        prev.gastosOriginal === nextSelectedData.gastosOriginal &&
        prev.totalOriginal === nextSelectedData.totalOriginal
          ? prev
          : nextSelectedData
      ))
      if (payload?.openModal) setOpenModal(true)
    },
    [erByMonthFromCodes]
  )

  // Inicializa selección ER con el último mes disponible
  useEffect(() => {
    if (erSelectedPeriod) return
    const months = monthlySummaryFromCodes?.uniquePeriods || []
    if (!months.length) return
    handleSelectMonthER(months[months.length - 1])
  }, [erSelectedPeriod, monthlySummaryFromCodes, handleSelectMonthER])

  const bankSourceData = bankTimeframe === 'week' ? weeklyChartData : monthlyChartData
  const chartDataBanco = useMemo(
    () => takeLastN(bankSourceData, monthsToShow),
    [bankSourceData, monthsToShow]
  )
  const agingChartDisplayData = useMemo(
    () => takeLastN(agingChartData, monthsToShow),
    [agingChartData, monthsToShow]
  )

  const handleSelectBanco = useCallback(payload => {
    const name = typeof payload === 'string' ? payload : payload?.name
    if (!name) return
    setBankSelectedPeriod(name)
    setOpenModalBanco(true)
  }, [])

  const onSelectBankOnly = useCallback(payload => {
    const name = typeof payload === 'string' ? payload : payload?.name
    if (!name) return
    setBankSelectedPeriod(name)
  }, [])

  // Selección robusta del período del banco
  useEffect(() => {
    const src = chartDataBanco
    if (!src?.length) return
    const last = src[src.length - 1]
    const candidate = last?.name ?? last
    if (!bankSelectedPeriod) {
      setBankSelectedPeriod(candidate)
      return
    }
    const exists = src.some(it => (it?.name ?? it) === bankSelectedPeriod)
    if (!exists) setBankSelectedPeriod(candidate)
  }, [chartDataBanco, bankSelectedPeriod])

  const weeklyBankDataForModal = useMemo(() => {
    if (!bankSelectedPeriod) return []
    if (isYYYYMM(bankSelectedPeriod)) {
      return weeklyChartData.filter(d => d.monthKey === bankSelectedPeriod)
    }
    if (isDayMonth(bankSelectedPeriod)) {
      return weeklyChartData.filter(d => dayjs(d.weekStart).format('DD/MM') === bankSelectedPeriod)
    }
    if (/^\d{4}-W\d{2}$/.test(bankSelectedPeriod)) {
      return weeklyChartData.filter(d => d.key === bankSelectedPeriod)
    }
    return weeklyChartData
  }, [bankSelectedPeriod, weeklyChartData])

  const bankModalTitle = useMemo(() => {
    if (!bankSelectedPeriod) return ''
    if (isDayMonth(bankSelectedPeriod)) return bankSelectedPeriod
    if (/^\d{4}-W\d{2}$/.test(bankSelectedPeriod)) {
      const item = weeklyChartData.find(d => d.key === bankSelectedPeriod)
      return item ? `${item.name}` : bankSelectedPeriod
    }
    return getMonthLabel(bankSelectedPeriod)
  }, [bankSelectedPeriod, weeklyChartData])

  const operationalCashFlow = useMemo(
    () => parseDisplayNumber(totales?.diferencia),
    [totales?.diferencia]
  )

  if (error && !hasLoadedData) {
    return (
      <div className='flex flex-col items-center justify-center h-full gap-4'>
        <h1 className='text-2xl font-bold'>Ha ocurrido un error</h1>
        <CustomButton
          onClick={refetch}
          title='Reintentar'
          color='primary'
          className='mt-4'
        />
      </div>
    )
  }

  // ---------- RENDER ----------
  return (
    <Grid
      container
      direction='column'
      rowSpacing={spacingZeroOrTwo}
      sx={{
        p: { xs: 1, sm: 2 },
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        '& .MuiGrid-item': { minWidth: 0 }
      }}
      className='font-sans text-text'
    >
      {error && hasLoadedData && (
        <Grid item xs={12}>
          <div
            role='alert'
            className='flex flex-col sm:flex-row items-center justify-between gap-3 rounded-card border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'
          >
            <span>No pudimos obtener la actualización. Seguimos mostrando los últimos datos disponibles.</span>
            <CustomButton onClick={refetch} title='Reintentar' color='primary' />
          </div>
        </Grid>
      )}
      <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%' }}>
        <Paper
          className='w-full min-w-0 max-w-full rounded-3xl bg-white shadow-full-xs px-2 sm:px-4 pt-0 pb-4'
          style={{
            height: 'auto',
            minHeight: isSummaryView ? 'auto' : '100vh',
            overflow: 'visible',
          }}
        >
          <Grid container spacing={spacingZeroOrTwo}>
            {/* TOP: 3 flip cards */}
            <Grid item xs={12}>
              <TopCobranzasMiniCards
                isSidebarConstrained={isSidebarConstrained}
                erIngresosAnual={erIngresosAnual}
                erGastosAnual={erGastosAnual}
                erIngresosUlt3={erIngresosUlt3}
                erGastosUlt3={erGastosUlt3}
                erIngresosMesActual={erIngresosMesActual}
                erGastosMesActual={erGastosMesActual}
                numberUi={numberUi}
              />
            </Grid>

            {/* <Grid item xs={12}>
              <div className='flex justify-center'>
                <div className='inline-grid w-full max-w-[420px] grid-cols-2 rounded-full border border-border bg-border/40 p-1'>
                  <button
                    type='button'
                    onClick={() => setActiveDashboardView('kpis')}
                    className={`rounded-full px-4 py-2 text-sm sm:text-base font-semibold transition-colors ${
                      activeDashboardView === 'kpis'
                        ? 'bg-primary text-surface shadow-card'
                        : 'text-muted hover:text-text'
                    }`}
                  >
                    KPIs
                  </button>
                  <button
                    type='button'
                    onClick={() => setActiveDashboardView('summary')}
                    className={`rounded-full px-4 py-2 text-sm sm:text-base font-semibold transition-colors ${
                      activeDashboardView === 'summary'
                        ? 'bg-primary text-surface shadow-card'
                        : 'text-muted hover:text-text'
                    }`}
                  >
                    Resumen Ejecutivo
                  </button>
                </div>
              </div>
            </Grid> */}

            {isSummaryView ? (
              <Grid item xs={12}>
                <ExecutiveSummaryView
                  currentMonthYearLabel={currentMonthYearLabel}
                  executiveAccent={executiveAccent}
                  executiveScore={executiveScore}
                  executiveStatus={executiveStatus}
                  totalSaldoAcumulado={totalSaldoAcumulado}
                  shouldCompactValues={shouldCompactValues}
                  parseDisplayNumber={parseDisplayNumber}
                  formatCLPFull={formatCLPFull}
                  formatCLPDisplay={formatCLPDisplay}
                  totales={totales}
                  runwayMonths={runwayMonths}
                  incomeDeltaPct={incomeDeltaPct}
                  formatDecimalDisplay={formatDecimalDisplay}
                  formatBaseDisplayValue={formatBaseDisplayValue}
                  volatilityLabel={volatilityLabel}
                  providerSummary={providerSummary}
                  topProviderAccounts={topProviderAccounts}
                  executiveScoreMeaning={executiveScoreMeaning}
                  executiveGoodBullet={executiveGoodBullet}
                  executiveImportantBullet={executiveImportantBullet}
                  executiveNextStepBullet={executiveNextStepBullet}
                  executiveFactors={executiveFactors}
                  executiveFactorExplainers={executiveFactorExplainers}
                  CustomTooltip={CustomTooltip}
                  kpiNumberClass={KPI_NUMBER_CLASS}
                />
              </Grid>
            ) : (
              <>
            {/* ER mensual (mismo cálculo que tabla) */}
            <Grid item xs={12} lg={9} id='chart-ingresos-egresos'>
              <div className={`flex flex-col justify-center h-full ${sectionCardClass}`}>
                <div className='flex items-center justify-between pb-4 sm:pb-6 w-full gap-2'>
                  <SectionTitle className='text-center flex-1'>
                    Ingresos vs Egresos (Últimos 12 meses)
                  </SectionTitle>
                  <div
                    className="w-11 h-11 md:w-9 md:h-9 rounded-full flex items-center justify-center bg-surface shadow-card cursor-pointer border border-border"
                    onClick={() => setOpenModalEstado(true)}
                  >
                    <img src={iconoEnlace} className="w-6 h-6 object-cover" />
                  </div>
                </div>
                {loading ? (
                  <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
                    <Lottie
                      animationData={LoaderMain}
                      loop
                      autoplay
                      style={{ width: 100, height: 100 }}
                    />
                  </Box>
                ) : (
                  <Box sx={chartScrollSxNoYScroll}>
                    <Box sx={chartInnerSx(520, 320)}>
                      <BarChartER
                        summaryData={monthlySummaryFromCodes}
                        visibleCount={monthsToShow}
                        onSelectMonth={handleSelectMonthER}
                      />
                    </Box>
                  </Box>
                )}
              </div>
            </Grid>

            {/* KPIs */}
            <Grid item xs={12} lg={3} id='chart-resultado-operativo'>
              <BarChartKPIs
                selectedMonthData={erSelectedData}
                cardPaddingClass={cardPaddingClass}
                isMobile={isMobile}
                erMonthLabel={erMonthLabel}
                numberUi={numberUi}
              />
            </Grid>

            {/* Tarjetas pequeñas */}
            <Grid item xs={12} container spacing={spacingOneOrTwo} alignItems='stretch'>
              <Grid item xs={12} md={6} lg={4}>
                <SemiPieCard
                  data={summaryDataClientes}
                  isNarrow={isNarrow}
                  smallCardBase={smallCardBase}
                  numberUi={numberUi}
                />
              </Grid>
            <Grid item xs={12} md={6} lg={4}>
                <DashboardCard className={`relative flex flex-col justify-center items-center px-4 sm:px-6 py-6 sm:py-8 ${smallCardBase}`}>
                  <div
                    className="absolute right-2 top-4 w-11 h-11 md:w-9 md:h-9 rounded-full flex items-center justify-center bg-surface shadow-card cursor-pointer border border-border"
                    onClick={onGoToResumen}
                  >
                    <img src={iconoEnlace} className="w-6 h-6 object-cover" />
                  </div>
                  <KpiCard
                    wrapper={false}
                    align='center'
                    value={
                      f29ProjectedValue != null ? (
                        <span
                          className={KPI_NUMBER_CLASS}
                          title={shouldCompactValues && Math.abs(parseDisplayNumber(f29ProjectedValue)) >= 1_000_000 ? `$ ${formatCLPFull(f29ProjectedValue)}` : undefined}
                        >
                          {`$ ${formatCLPDisplay(f29ProjectedValue)}`}
                        </span>
                      ) : null
                    }
                    tone='primary'
                    size='secondary'
                    className='text-center'
                    valueClassName={`${KPI_NUMBER_CLASS} text-[clamp(1.8rem,6vw,2.5rem)]`}
                  />
                  <h2 className="text-xs sm:text-sm text-center text-muted pt-4 sm:pt-6 flex flex-wrap items-center justify-center gap-1">
                    Proyección F29{' '}
                    <span className="inline-flex items-center gap-1">
                      {mesActual}
                      <CustomTooltip title="Advertencia: Esta proyección del Formulario 29 es un cálculo estimado elaborado en base a la información disponible al momento de la consulta. Corresponde únicamente a una simulación y no debe utilizarse como información oficial para declarar ante el Servicio de Impuestos Internos.
                        Los resultados pueden contener inconsistencias o faltar datos necesarios para una determinación correcta de los impuestos. Te recomendamos consultar siempre a tu contador o asesor tributario antes de presentar cualquier declaración formal.">
                        <QuestionIcon
                          fontSize="small"
                          className="text-muted cursor-pointer hover:text-text"
                        />
                      </CustomTooltip>
                    </span>
                  </h2>
                </DashboardCard>
            </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <DashboardCard className={`flex flex-col justify-center items-center px-4 sm:px-6 py-6 sm:py-8 ${smallCardBase}`}>
                  {/* Resultado anual */}
                  <div className='flex flex-col items-center gap-1'>
                    <KpiCard
                      wrapper={false}
                      align='center'
                      value={
                        <span
                          className={KPI_NUMBER_CLASS}
                          title={
                            shouldCompactValues && Math.abs(parseDisplayNumber(totalSaldoAcumulado)) >= 1_000_000
                              ? `${totalSaldoAcumulado >= 0 ? '+' : ''}${formatCLPFull(totalSaldoAcumulado)}`
                              : undefined
                          }
                        >
                          {`${totalSaldoAcumulado >= 0 ? '+' : ''}${formatCLPDisplay(totalSaldoAcumulado)}`}
                        </span>
                      }
                      tone={totalSaldoAcumulado >= 0 ? 'primary' : 'danger'}
                      size='secondary'
                      valueClassName={`${KPI_NUMBER_CLASS} text-[clamp(1.8rem,5vw,2.4rem)]`}
                    />
                    <h2 className='text-xs sm:text-sm text-muted text-center'>
                      Resultado anual
                    </h2>
                  </div>

                  {/* Separación entre secciones */}
                  <div className='h-6' />

                  {/* Margen operativo */}
                  <div className='flex flex-col items-center gap-1'>
                    <KpiCard
                      wrapper={false}
                      align='center'
                      value={
                        <span className={KPI_NUMBER_CLASS} title={porcentajeLabel}>
                          {porcentajeLabel}
                        </span>
                      }
                      tone={porcentajeValor >= 0 ? 'primary' : 'danger'}
                      size='secondary'
                      valueClassName={`${KPI_NUMBER_CLASS} text-[clamp(1.6rem,4.5vw,2.2rem)]`}
                    />
                    <h2 className='text-xs sm:text-sm text-center text-muted'>
                      Margen Operativo
                    </h2>
                  </div>

                </DashboardCard>
              </Grid>
            </Grid>

            {/* Banco */}
            <Grid item xs={12}>
              {loading ? (
                <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
                  <Lottie
                    animationData={LoaderMain}
                    loop
                    autoplay
                    style={{ width: 100, height: 100 }}
                  />
                </Box>
              ) : (
                <SimpleChartCard
                  data={chartDataBanco}
                  onSelectMonth={handleSelectBanco}
                  selectedMonth={bankSelectedPeriod}
                  bankTimeframe={bankTimeframe}
                  onChangeTimeframe={handleBankTimeframe}
                  isCompact={isCompact}
                  isMobile={isMobile}
                  chartScrollSx={chartScrollSx}
                  chartInnerSx={chartInnerSx}
                  totales={totales}
                  operationalCashFlow={operationalCashFlow}
                  promedioMensual={promedioMensual}
                  runAway={runAway}
                  tooltipIconClass={tooltipIconClass}
                  CustomTooltip={CustomTooltip}
                  getMonthLabel={getMonthLabel}
                  SimpleBarChart={SimpleBarChart}
                  numberUi={numberUi}
                  onOpenInitialBalanceModal={handleOpenInitialBalanceModal}
                  initialBalanceRows={initialBalanceRows}
                  initialBalancesLoading={initialBalancesLoading}
                />
              )}
            </Grid>

            <Grid item xs={12} id='chart-presupuesto-caja'>
              <CashFlowDueLineChart
                data={receivablesDueChartData}
                title='Proyección presupuesto de caja'
                vencidoPorCobrar={vencidoPorCobrar}
                vencidoPorPagar={vencidoPorPagar}
              />
            </Grid>

            {/* Envejecimiento */}
            <Grid item xs={12} id='chart-envejecimiento-deuda'>
              <div className={`flex flex-col justify-center h-full ${sectionCardClass}`}>
                <div className='flex flex-col justify-center items-center pb-4 sm:pb-6'>
                  <SectionTitle className='text-center'>
                    Envejecimiento de la deuda (Últimos 12 meses)
                  </SectionTitle>
                </div>
                  <Box sx={chartScrollSxNoYScroll}>
                  <Box sx={chartInnerSx(520, 260)}>
                    <ChartStackedAging data={agingChartDisplayData} onSelectMonth={noopOnSelectMonth} />
                  </Box>
                </Box>
              </div>
            </Grid>

            <Grid
              item
              xs={12}
              id='estado-resultado-resumen'
              sx={{ minWidth: 0, maxWidth: '100%' }}
            >
              <EstadoResultadoResumenTable
                summary={estadoResultadoResumen}
                loading={loading}
              />
            </Grid>
              </>
            )}
          </Grid>

          {/* MODAL ER */}
          <Dialog
            open={openModal}
            PaperComponent={isCompact ? undefined : DraggablePaper}
            fullScreen={isCompact}
            scroll={isCompact ? 'paper' : 'body'}
            maxWidth={false}
            PaperProps={{ sx: dialogPaperSx }}
          >
            <div
              id='draggable-dialog-title'
              className={`flex ${isCompact ? 'flex-col gap-4' : 'items-center'} p-4 sm:p-6 shadow-card bg-surface border border-border relative w-full h-full ${isCompact ? '' : 'rounded-card'}`}
            >
              {erSelectedPeriod && (
                isCompact ? (
                  <div className='flex flex-col w-full h-full gap-4'>
                    <div className='flex items-center justify-between gap-3'>
                      <h1 className='text-sm sm:text-base font-semibold border border-border text-text rounded-full px-4 py-1'>
                        {erMonthLabel}
                      </h1>
                      <CustomButton
                        onClick={closeERModal}
                        className='px-3 py-2 rounded-full text-xs sm:text-sm'
                        title='CERRAR'
                        color='primary'
                      />
                    </div>
                    <Box sx={chartScrollSx}>
                        <Box sx={chartInnerSx(520, 240)}>
                        <BarChartER summaryData={weeklyER} visibleCount={monthsToShow} onSelectMonth={noopOnSelectMonth} />
                      </Box>
                    </Box>
                    <div className='w-full'>
                      <CardInfo
                        title='Resultado Operativo Período'
                        amount={erSelectedData.totalOriginal}
                        isMain
                        numberUi={numberUi}
                      />
                      <div className='mt-3'>
                        <CardInfo
                          title='Ingreso Mes'
                          amount={erSelectedData.ingresosOriginal}
                          customColor='text-success'
                          numberUi={numberUi}
                        />
                        <CardInfo
                          title='Gasto Mes'
                          amount={erSelectedData.gastosOriginal}
                          customColor='text-danger'
                          numberUi={numberUi}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className='absolute top-8 left-6'>
                      <h1 className='text-base font-semibold border border-border text-text rounded-full px-12'>
                        {erMonthLabel}
                      </h1>
                    </div>
                    <div className='flex-1 flex justify-center items-center pr-36 pt-12'>
                      <Box sx={chartScrollSx}>
                        <Box sx={chartInnerSx(520, 260)}>
                          <BarChartER summaryData={weeklyER} visibleCount={monthsToShow} onSelectMonth={noopOnSelectMonth} />
                        </Box>
                      </Box>
                    </div>
                    <div className='absolute top-6 right-6 text-right'>
                      <CardInfo
                        title='Resultado Operativo Período'
                        amount={erSelectedData.totalOriginal}
                        isMain
                        numberUi={numberUi}
                      />
                      <div className='mt-4'>
                        <CardInfo
                          title='Ingreso Mes'
                          amount={erSelectedData.ingresosOriginal}
                          customColor='text-success'
                          numberUi={numberUi}
                        />
                        <CardInfo
                          title='Gasto Mes'
                          amount={erSelectedData.gastosOriginal}
                          customColor='text-danger'
                          numberUi={numberUi}
                        />
                      </div>
                    </div>
                    <div className='absolute bottom-4 right-6'>
                      <CustomButton
                        onClick={closeERModal}
                        className='px-4 py-2 rounded-full text-sm'
                        title='CERRAR'
                        color='primary'
                      />
                    </div>
                  </>
                )
              )}
            </div>
          </Dialog>

          <InitialBalancesModal
            isCompact={isCompact}
            controller={initialBalancesController}
          />

          {/* MODAL BANCO */}
          <Dialog
            open={openModalBanco}
            PaperComponent={isCompact ? undefined : DraggablePaper}
            fullScreen={isCompact}
            scroll={isCompact ? 'paper' : 'body'}
            maxWidth={false}
            PaperProps={{ sx: dialogPaperSx }}
          >
            <div
              id='draggable-dialog-title'
              className={`flex ${isCompact ? 'flex-col gap-4' : 'items-center'} p-4 sm:p-6 shadow-card bg-surface border border-border items-center relative w-full h-full ${isCompact ? '' : 'rounded-card'}`}
            >
              {bankSelectedPeriod && (
                isCompact ? (
                  <div className='flex flex-col w-full h-full gap-4'>
                    <div className='flex items-center justify-between gap-3'>
                      <h1 className='text-sm sm:text-base font-semibold border border-border text-text rounded-full px-4 py-1'>
                        {bankModalTitle}
                      </h1>
                      <CustomButton
                        onClick={closeBancoModal}
                        className='px-3 py-2 rounded-full text-xs sm:text-sm'
                        title='CERRAR'
                        color='primary'
                      />
                    </div>
                    <Box sx={chartScrollSx}>
                      <Box sx={chartInnerSx(520, 240)}>
                        <SimpleBarChart
                          data={weeklyBankDataForModal}
                          onSelectMonth={onSelectBankOnly}
                          selectedMonth={bankSelectedPeriod}
                        />
                      </Box>
                    </Box>
                    <div className='w-full'>
                      <CashFlowSummaryPanel
                        operationalCashFlowAmount={operationalCashFlow}
                        promedioMensual={promedioMensual}
                        runAway={runAway}
                        tooltipIconClass={tooltipIconClass}
                        CustomTooltip={CustomTooltip}
                        numberUi={numberUi}
                        hasInitialBalances={hasInitialBalances}
                        initialBalancesLoading={initialBalancesLoading}
                        onOpenInitialBalanceModal={handleOpenInitialBalanceModal}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className='absolute top-8 left-6'>
                      <h1 className='text-base font-semibold border border-border text-text rounded-full px-12'>
                        {bankModalTitle}
                      </h1>
                    </div>
                    <div className='flex-1 flex justify-center items-center pr-36 pt-12'>
                      <Box sx={chartScrollSx}>
                        <Box sx={chartInnerSx(520, 260)}>
                          <SimpleBarChart
                            data={weeklyBankDataForModal}
                            onSelectMonth={onSelectBankOnly}
                            selectedMonth={bankSelectedPeriod}
                          />
                        </Box>
                      </Box>
                    </div>
                    <div className='absolute top-6 right-6 w-full max-w-[320px] text-right'>
                      <CashFlowSummaryPanel
                        operationalCashFlowAmount={operationalCashFlow}
                        promedioMensual={promedioMensual}
                        runAway={runAway}
                        tooltipIconClass={tooltipIconClass}
                        CustomTooltip={CustomTooltip}
                        numberUi={numberUi}
                        hasInitialBalances={hasInitialBalances}
                        initialBalancesLoading={initialBalancesLoading}
                        onOpenInitialBalanceModal={handleOpenInitialBalanceModal}
                      />
                    </div>
                    <div className='absolute bottom-4 right-6'>
                      <CustomButton
                        onClick={closeBancoModal}
                        className='px-4 py-2 rounded-full text-sm'
                        title='CERRAR'
                        color='primary'
                      />
                    </div>
                  </>
                )
              )}
            </div>
          </Dialog>
          
          {/*MODAL ESTADO*/}
          <ResizableModal
            open={openModalEstado}
            onClose={closeEstadoModal}
            autoFullscreen={isCompact}
          >
              <ListEstadoDeResultadosExtend />
          </ResizableModal>

          {!isSummaryView && (
            <Grid item xs={12}>
              <FooterVisionDelNegocio
                summaryData={resultado}
                stateIngresoEgreso={loading}
              />
            </Grid>
          )}
        </Paper>
      </Grid>
    </Grid>
  )
}

export default VisionDelNegocio
